/**
 * Deep links al registro exacto del sistema de origen.
 *
 * Regla: toda entidad que vive en otro sistema linkea a SU registro, nunca al
 * home ni a una búsqueda. Un seguimiento abre su work item en Plane; un cliente
 * abre su lead en Close; una suscripción abre su customer en Stripe.
 *
 * Cuando falta el id externo NO se renderiza un link roto: el builder devuelve
 * null y la UI muestra el estado "sin vincular" con la acción de vincularlo,
 * igual que un KPI sin dato.
 */

import type { MotivoSinVincular } from './links.js'
import { RUTA_ADMIN } from './rutas.js'

export type SystemOfRecord =
  | 'plane'
  | 'close'
  | 'stripe'
  | 'plataforma'
  | 'search_console'
  | 'langfuse'

export interface DeepLinkConfig {
  plane: { baseUrl: string; workspaceSlug: string; projectId: string }
  close: { baseUrl: string }
  stripe: { baseUrl: string }
  plataforma: { baseUrl: string }
  searchConsole: { baseUrl: string; resourceId: string }
  /** Quinto sistema de origen, el del módulo `/usage`. Ver `DEFAULT_DEEP_LINK_CONFIG`. */
  langfuse: { baseUrl: string; projectId: string }
}

export interface DeepLink {
  system: SystemOfRecord
  url: string
  /** Texto del link, ej. "OPS-142 en Plane" */
  label: string
  /** Nombre legible del sistema, para el aviso de pestaña nueva */
  systemLabel: string
}

/**
 * Nombre de cada sistema tal como se lee en la frase
 * "(se abre en {systemLabel}, pestaña nueva)".
 */
export const SYSTEM_LABELS: Record<SystemOfRecord, string> = {
  plane: 'Plane',
  close: 'Close',
  stripe: 'Stripe',
  plataforma: 'la plataforma de Laburen',
  search_console: 'Search Console',
  langfuse: 'Langfuse',
}

/** Bases por defecto. Se sobreescriben por variable de entorno. */
export const DEFAULT_DEEP_LINK_CONFIG: DeepLinkConfig = {
  plane: {
    baseUrl: 'https://app.plane.so',
    workspaceSlug: 'laburen',
    projectId: '',
  },
  close: { baseUrl: 'https://app.close.com' },
  stripe: { baseUrl: 'https://dashboard.stripe.com' },
  plataforma: { baseUrl: 'https://app.laburen.com' },
  searchConsole: {
    baseUrl: 'https://search.google.com/search-console',
    resourceId: '',
  },
  /**
   * **Los dos vacíos a propósito, no es un olvido.** A diferencia de Plane o
   * Close, acá ni siquiera la base está decidida: falta confirmar si el Langfuse
   * de Laburen es cloud (`https://cloud.langfuse.com`) o self-hosted, y cuál es
   * el id del proyecto. Poner `cloud.langfuse.com` "por las dudas" produciría
   * decenas de links que abren un login ajeno y parecen rotos por otra razón.
   *
   * Vacío significa "sin vincular", que es lo único cierto hoy: los builders
   * devuelven `null` y la UI muestra el estado con `motivoLangfuse()`, que nombra
   * la variable que falta y manda a Admin a cargarla. Es el mismo tratamiento que
   * `SEARCH_CONSOLE_RESOURCE_ID`.
   */
  langfuse: { baseUrl: '', projectId: '' },
}

/** Merge superficial por sistema sobre los defaults. */
export function deepLinkConfig(
  overrides: DeepPartialConfig = {},
): DeepLinkConfig {
  return {
    plane: { ...DEFAULT_DEEP_LINK_CONFIG.plane, ...overrides.plane },
    close: { ...DEFAULT_DEEP_LINK_CONFIG.close, ...overrides.close },
    stripe: { ...DEFAULT_DEEP_LINK_CONFIG.stripe, ...overrides.stripe },
    plataforma: {
      ...DEFAULT_DEEP_LINK_CONFIG.plataforma,
      ...overrides.plataforma,
    },
    searchConsole: {
      ...DEFAULT_DEEP_LINK_CONFIG.searchConsole,
      ...overrides.searchConsole,
    },
    langfuse: { ...DEFAULT_DEEP_LINK_CONFIG.langfuse, ...overrides.langfuse },
  }
}

export type DeepPartialConfig = {
  [K in keyof DeepLinkConfig]?: Partial<DeepLinkConfig[K]>
}

/**
 * Variables de entorno que sobreescriben las bases. Se leen en el Worker y
 * viajan al front dentro de `AppConfig`, para que el front nunca hardcodee
 * una URL de un sistema de origen.
 */
export interface DeepLinkEnv {
  PLANE_BASE_URL?: string | undefined
  PLANE_WORKSPACE_SLUG?: string | undefined
  PLANE_PROJECT_ID?: string | undefined
  CLOSE_BASE_URL?: string | undefined
  STRIPE_BASE_URL?: string | undefined
  PLATAFORMA_BASE_URL?: string | undefined
  SEARCH_CONSOLE_BASE_URL?: string | undefined
  SEARCH_CONSOLE_RESOURCE_ID?: string | undefined
  LANGFUSE_BASE_URL?: string | undefined
  LANGFUSE_PROJECT_ID?: string | undefined
}

export function deepLinkConfigFromEnv(env: DeepLinkEnv): DeepLinkConfig {
  return deepLinkConfig({
    plane: {
      ...pick('baseUrl', env.PLANE_BASE_URL),
      ...pick('workspaceSlug', env.PLANE_WORKSPACE_SLUG),
      ...pick('projectId', env.PLANE_PROJECT_ID),
    },
    close: pick('baseUrl', env.CLOSE_BASE_URL),
    stripe: pick('baseUrl', env.STRIPE_BASE_URL),
    plataforma: pick('baseUrl', env.PLATAFORMA_BASE_URL),
    searchConsole: {
      ...pick('baseUrl', env.SEARCH_CONSOLE_BASE_URL),
      ...pick('resourceId', env.SEARCH_CONSOLE_RESOURCE_ID),
    },
    langfuse: {
      ...pick('baseUrl', env.LANGFUSE_BASE_URL),
      ...pick('projectId', env.LANGFUSE_PROJECT_ID),
    },
  })
}

function pick<K extends string>(
  key: K,
  value: string | undefined,
): Record<K, string> | Record<string, never> {
  const limpio = normalizeId(value ?? null)
  return limpio === null ? {} : ({ [key]: limpio } as Record<K, string>)
}

/**
 * Un id ausente, vacío o con solo espacios es "sin vincular". Tratarlos igual
 * evita el caso `.../lead//` que parece un link y lleva a una 404.
 */
function normalizeId(id: string | null | undefined): string | null {
  if (id === null || id === undefined) return null
  const limpio = id.trim()
  return limpio.length === 0 ? null : limpio
}

function trimSlash(base: string): string {
  return base.replace(/\/+$/, '')
}

/**
 * La base de un sistema, o `null` si no hay ninguna.
 *
 * Sin esto una base vacía —config editada a mano, variable puesta en blanco—
 * producía una URL relativa (`/lead/lead_abc/`) que parece un link y en realidad
 * navega dentro del panel. Es el mismo defecto que un id ausente, así que se
 * resuelve igual: el builder devuelve null y la UI muestra "sin vincular".
 */
function base(baseUrl: string | null | undefined): string | null {
  const limpio = normalizeId(baseUrl)
  return limpio === null ? null : trimSlash(limpio)
}

function seg(value: string): string {
  return encodeURIComponent(value)
}

// ---------------------------------------------------------------------------
// Builders. Uno por tipo de registro. Todos puros; null si falta el id.
// ---------------------------------------------------------------------------

export interface PlaneWorkItemOptions {
  /** Proyecto guardado en la fila. Solo se usa si la config no declara uno. */
  projectId?: string | null
  /** Identificador legible, ej. "OPS-142". Es el label del link. */
  identificador?: string | null
}

/**
 * Proyecto de Plane que gobierna la URL de un work item.
 *
 * **Manda la config.** El panel apunta a un único proyecto de Plane y la
 * coordenada la declara `PLANE_PROJECT_ID`; el valor guardado en la fila es
 * histórico —lo escribió el seed o una config anterior— y cuando difiere del
 * configurado produce URLs a un proyecto que no es el del workspace. Eso fue
 * exactamente lo que pasó: 17 de 18 links apuntaban a `projects/ops-panel`
 * mientras la config declaraba el uuid confirmado por el founder.
 *
 * La fila queda como respaldo para el entorno sin configurar: sin
 * `PLANE_PROJECT_ID` un seguimiento viejo sigue linkeando a donde vive, en vez
 * de quedarse sin link.
 */
export function planeProjectIdFor(
  cfg: DeepLinkConfig,
  guardado: string | null = null,
): string | null {
  return normalizeId(cfg.plane.projectId) ?? normalizeId(guardado)
}

/** `{baseUrl}/{workspaceSlug}/projects/{projectId}/issues/{id}` */
export function planeWorkItem(
  cfg: DeepLinkConfig,
  workItemId: string | null,
  options: PlaneWorkItemOptions = {},
): DeepLink | null {
  const id = normalizeId(workItemId)
  const raiz = base(cfg.plane.baseUrl)
  const workspaceSlug = normalizeId(cfg.plane.workspaceSlug)
  const projectId = planeProjectIdFor(cfg, options.projectId ?? null)
  if (id === null || raiz === null || workspaceSlug === null || projectId === null) {
    return null
  }

  const identificador = normalizeId(options.identificador ?? null)
  return {
    system: 'plane',
    url: `${raiz}/${seg(workspaceSlug)}/projects/${seg(projectId)}/issues/${seg(id)}`,
    label: `${identificador ?? 'Seguimiento'} en Plane`,
    systemLabel: SYSTEM_LABELS.plane,
  }
}

/** `{baseUrl}/lead/{leadId}/` */
export function closeLead(
  cfg: DeepLinkConfig,
  leadId: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const id = normalizeId(leadId)
  const raiz = base(cfg.close.baseUrl)
  if (id === null || raiz === null) return null

  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'close',
    url: `${raiz}/lead/${seg(id)}/`,
    label: `${nombre ?? 'Lead'} en Close`,
    systemLabel: SYSTEM_LABELS.close,
  }
}

/**
 * La oportunidad no tiene página propia en Close: vive dentro de la página del
 * lead, así que el link es el del lead más el fragmento de la oportunidad.
 * Necesita los dos ids: sin el lead no hay página a la que ir.
 */
export function closeOpportunity(
  cfg: DeepLinkConfig,
  leadId: string | null,
  opportunityId: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const lead = normalizeId(leadId)
  const opp = normalizeId(opportunityId)
  const raiz = base(cfg.close.baseUrl)
  if (lead === null || opp === null || raiz === null) return null

  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'close',
    url: `${raiz}/lead/${seg(lead)}/#opportunity-${seg(opp)}`,
    label: `${nombre ?? 'Oportunidad'} en Close`,
    systemLabel: SYSTEM_LABELS.close,
  }
}

/** `{baseUrl}/customers/{customerId}` */
export function stripeCustomer(
  cfg: DeepLinkConfig,
  customerId: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const id = normalizeId(customerId)
  const raiz = base(cfg.stripe.baseUrl)
  if (id === null || raiz === null) return null

  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'stripe',
    url: `${raiz}/customers/${seg(id)}`,
    label: `${nombre ?? 'Customer'} en Stripe`,
    systemLabel: SYSTEM_LABELS.stripe,
  }
}

/** `{baseUrl}/subscriptions/{subscriptionId}` */
export function stripeSubscription(
  cfg: DeepLinkConfig,
  subscriptionId: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const id = normalizeId(subscriptionId)
  const raiz = base(cfg.stripe.baseUrl)
  if (id === null || raiz === null) return null

  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'stripe',
    url: `${raiz}/subscriptions/${seg(id)}`,
    label: `${nombre ?? 'Suscripción'} en Stripe`,
    systemLabel: SYSTEM_LABELS.stripe,
  }
}

/** `{baseUrl}/clientes/{clientId}` — ficha del cliente en la plataforma. */
export function plataformaCliente(
  cfg: DeepLinkConfig,
  clientId: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const id = normalizeId(clientId)
  const raiz = base(cfg.plataforma.baseUrl)
  if (id === null || raiz === null) return null

  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'plataforma',
    url: `${raiz}/clientes/${seg(id)}`,
    label: `${nombre ?? 'Cliente'} en la plataforma`,
    systemLabel: SYSTEM_LABELS.plataforma,
  }
}

/** `{baseUrl}/agentes/{agentId}` — ficha del empleado digital. */
export function plataformaAgente(
  cfg: DeepLinkConfig,
  agentId: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const id = normalizeId(agentId)
  const raiz = base(cfg.plataforma.baseUrl)
  if (id === null || raiz === null) return null

  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'plataforma',
    url: `${raiz}/agentes/${seg(id)}`,
    label: `${nombre ?? 'Empleado digital'} en la plataforma`,
    systemLabel: SYSTEM_LABELS.plataforma,
  }
}

/**
 * Informe de rendimiento de Search Console con el filtro de query aplicado.
 * El prefijo `!` es el operador de coincidencia exacta de Search Console: sin
 * él la vista abre en "contiene" y muestra queries que no son la nuestra.
 */
export function searchConsoleQuery(
  cfg: DeepLinkConfig,
  query: string | null,
): DeepLink | null {
  const q = normalizeId(query)
  const raiz = base(cfg.searchConsole.baseUrl)
  const resourceId = normalizeId(cfg.searchConsole.resourceId)
  if (q === null || raiz === null || resourceId === null) return null

  const params = new URLSearchParams({
    resource_id: resourceId,
    query: `!${q}`,
  })
  return {
    system: 'search_console',
    url: `${raiz}/performance/search-analytics?${params.toString()}`,
    label: `"${q}" en Search Console`,
    systemLabel: SYSTEM_LABELS.search_console,
  }
}

/**
 * Por qué una query de SEO se quedó sin link, o `null` si sí lo tiene.
 *
 * Lo que falta no es un id por query —el texto de la query ES su clave en
 * Search Console— sino **la propiedad**: una sola variable
 * (`SEARCH_CONSOLE_RESOURCE_ID`) que hoy está vacía a propósito, porque no está
 * confirmado si la propiedad es de dominio (`sc-domain:laburen.com`) o de
 * prefijo de URL. Mientras siga vacía, las quince queries muestran el mismo
 * estado y el pedido tiene que ser uno solo: cargar la variable en Admin.
 */
export function motivoSearchConsole(
  cfg: DeepLinkConfig,
): MotivoSinVincular | null {
  if (normalizeId(cfg.searchConsole.resourceId) !== null) return null
  return {
    tipo: 'config_global',
    envVar: 'SEARCH_CONSOLE_RESOURCE_ID',
    mensaje:
      'Falta configurar la propiedad de Search Console en Admin: sin ella ninguna query puede abrir su informe.',
    accion: {
      label: 'Configurar la propiedad de Search Console',
      ruta: RUTA_ADMIN,
    },
  }
}

// ---------------------------------------------------------------------------
// Langfuse — quinto sistema de origen, el del módulo /usage
// ---------------------------------------------------------------------------

/**
 * Rango ISO de un período 'YYYY-MM': `[desde, hasta)`, en UTC.
 *
 * `hasta` es el primer instante del mes siguiente y no el último del mes: un
 * filtro `<= 2026-07-31T23:59:59` se come las trazas del último segundo, y esas
 * trazas son consumo que alguien pagó.
 */
export function rangoDePeriodo(
  periodo: string | null,
): { desde: string; hasta: string } | null {
  const limpio = normalizeId(periodo)
  if (limpio === null) return null
  const m = /^(\d{4})-(\d{2})$/.exec(limpio)
  if (m === null) return null
  const anio = Number(m[1])
  const mes = Number(m[2])
  if (mes < 1 || mes > 12) return null
  return {
    desde: new Date(Date.UTC(anio, mes - 1, 1)).toISOString(),
    hasta: new Date(Date.UTC(anio, mes, 1)).toISOString(),
  }
}

/**
 * Raíz del proyecto en Langfuse, o `null` si falta la base o el id.
 *
 * Los tres builders pasan por acá para que el caso "sin vincular" sea uno solo:
 * hoy los dos valores están vacíos y ninguno de los tres devuelve link.
 */
function langfuseBase(cfg: DeepLinkConfig): string | null {
  const raiz = base(cfg.langfuse.baseUrl)
  const projectId = normalizeId(cfg.langfuse.projectId)
  if (raiz === null || projectId === null) return null
  return `${raiz}/project/${seg(projectId)}`
}

/** `{baseUrl}/project/{projectId}/traces/{traceId}` — la traza exacta. */
export function langfuseTrace(
  cfg: DeepLinkConfig,
  traceId: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const id = normalizeId(traceId)
  const raiz = langfuseBase(cfg)
  if (id === null || raiz === null) return null

  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'langfuse',
    url: `${raiz}/traces/${seg(id)}`,
    label: `${nombre ?? 'Traza'} en Langfuse`,
    systemLabel: SYSTEM_LABELS.langfuse,
  }
}

/**
 * Sesiones de un agente **con el filtro de período ya aplicado**.
 *
 * El cruce agente↔consumo se hace por el prefijo de sesión que la plataforma
 * estampa (`langfuse_session_prefix`); sin prefijo no hay forma de saber qué
 * sesiones son de ese agente, así que el link es `null` y no una lista de todas
 * las sesiones del proyecto —que se leería como "estas son las suyas".
 *
 * Los nombres de los parámetros de filtro son los de la tabla de sesiones de
 * Langfuse y quedan por confirmar junto con la instancia: hoy no se puede
 * verificar contra nada porque `LANGFUSE_BASE_URL` está vacío a propósito.
 */
export function langfuseSessionesDeAgente(
  cfg: DeepLinkConfig,
  sessionPrefix: string | null,
  desde: string | null,
  hasta: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const prefijo = normalizeId(sessionPrefix)
  const raiz = langfuseBase(cfg)
  const from = normalizeId(desde)
  const to = normalizeId(hasta)
  if (prefijo === null || raiz === null || from === null || to === null) return null

  const params = new URLSearchParams({
    search: prefijo,
    fromTimestamp: from,
    toTimestamp: to,
  })
  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'langfuse',
    url: `${raiz}/sessions?${params.toString()}`,
    label: `Sesiones de ${nombre ?? 'el agente'} en Langfuse`,
    systemLabel: SYSTEM_LABELS.langfuse,
  }
}

/** Uso de un modelo en el período: observaciones filtradas por modelo y fecha. */
export function langfuseModelo(
  cfg: DeepLinkConfig,
  modelId: string | null,
  periodo: string | null,
  options: { nombre?: string | null } = {},
): DeepLink | null {
  const id = normalizeId(modelId)
  const raiz = langfuseBase(cfg)
  const rango = rangoDePeriodo(periodo)
  if (id === null || raiz === null || rango === null) return null

  const params = new URLSearchParams({
    model: id,
    fromTimestamp: rango.desde,
    toTimestamp: rango.hasta,
  })
  const nombre = normalizeId(options.nombre ?? null)
  return {
    system: 'langfuse',
    url: `${raiz}/observations?${params.toString()}`,
    label: `${nombre ?? id} en Langfuse`,
    systemLabel: SYSTEM_LABELS.langfuse,
  }
}

/**
 * Por qué un registro de `/usage` se quedó sin link a Langfuse, o `null` si sí
 * lo tiene.
 *
 * Igual que con Search Console, lo que falta no es un id por fila: son **dos
 * variables** que gobiernan todos los links del módulo. Sin esto la pantalla
 * pediría sesenta veces lo mismo. Se nombra la primera que falte, que es la que
 * hay que cargar primero.
 */
export function motivoLangfuse(cfg: DeepLinkConfig): MotivoSinVincular | null {
  const raiz = normalizeId(cfg.langfuse.baseUrl)
  const projectId = normalizeId(cfg.langfuse.projectId)
  if (raiz !== null && projectId !== null) return null

  return {
    tipo: 'config_global',
    envVar: raiz === null ? 'LANGFUSE_BASE_URL' : 'LANGFUSE_PROJECT_ID',
    mensaje:
      raiz === null
        ? 'Falta configurar Langfuse en Admin: no está confirmado si la instancia es cloud o self-hosted, y sin la base ninguna traza puede abrirse.'
        : 'Falta el id del proyecto de Langfuse en Admin: sin él ninguna traza puede abrirse.',
    accion: { label: 'Configurar Langfuse', ruta: RUTA_ADMIN },
  }
}

/**
 * Texto accesible del salto de sistema. Va dentro de un `<span className="sr-only">`
 * pegado al link: el aviso nunca queda solo en un icono.
 */
export function avisoPestanaNueva(link: DeepLink): string {
  return ` (se abre en ${link.systemLabel}, pestaña nueva)`
}

/** Atributos obligatorios de todo link externo. */
export const EXTERNAL_LINK_ATTRS = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const
